import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import RichTextEditor from '../RichTextEditor';
import { Upload, X, Shield, Users, DollarSign, Send, Plus, Trash2 } from 'lucide-react';
import { AGE_CATEGORIES, TOURNAMENT_STYLES } from './constants';

export const AdminTournamentFormDialog = ({
  showCreateDialog,
  setShowCreateDialog,
  showEditDialog,
  setShowEditDialog,
  formData,
  setFormData,
  imagePreview,
  removeImage,
  handleImageSelect,
  resetForm,
  handleCreate,
  handleUpdate,
  programs = [],
  toggleAgeCategory,
  toggleTargetProgram,
}) => {
  return (
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Tournament' : 'Create Tournament'}</DialogTitle>
            <DialogDescription>
              {showEditDialog ? 'Update tournament details' : 'Create a new tournament'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Tournament Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter tournament name"
                  data-testid="tournament-name-input"
                />
              </div>
              
              {/* Tournament Image Upload */}
              <div className="col-span-2">
                <Label>Tournament Image</Label>
                <div className="mt-2">
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img 
                        src={imagePreview} 
                        alt="Tournament preview" 
                        className="h-32 w-auto rounded-lg border object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={removeImage}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-slate-400" />
                        <p className="text-sm text-slate-500">
                          <span className="font-semibold">Click to upload</span> tournament image
                        </p>
                        <p className="text-xs text-slate-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageSelect}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label>Year</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  data-testid="tournament-year-input"
                />
              </div>

              <div>
                <Label>Travel Type</Label>
                <Select
                  value={formData.location_type}
                  onValueChange={(value) => {
                    // Auto-set competition style for out_of_state (travel) tournaments
                    const newStyle = value === 'out_of_state' ? 'competition' : formData.tournament_style;
                    setFormData({ ...formData, location_type: value, tournament_style: newStyle });
                  }}
                >
                  <SelectTrigger data-testid="location-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">In State</SelectItem>
                    <SelectItem value="out_of_state">Out of State</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Location</Label>
                <Input
                  placeholder="e.g., Virginia, NJ, MD, Philly"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="tournament-location-input"
                />
              </div>

              {/* Tournament Style and Age Eligibility Cutoff in one row */}
              <div>
                <Label>Tournament Style</Label>
                <Select
                  value={formData.tournament_style}
                  onValueChange={(value) => setFormData({ ...formData, tournament_style: value })}
                >
                  <SelectTrigger data-testid="tournament-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TOURNAMENT_STYLES.map(style => (
                      <SelectItem key={style.value} value={style.value}>
                        {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {TOURNAMENT_STYLES.find(s => s.value === formData.tournament_style)?.description}
                </p>
                {formData.location_type === 'out_of_state' && (
                  <p className="text-xs text-blue-600 mt-1">
                    ℹ️ Travel tournaments do not have divisions. Coach assigns teams directly.
                  </p>
                )}
              </div>

              <div>
                <Label>Age Eligibility Cutoff Date</Label>
                <Input
                  type="date"
                  value={formData.age_cutoff_date}
                  onChange={(e) => setFormData({ ...formData, age_cutoff_date: e.target.value })}
                  placeholder={`Default: Sept 1, ${formData.year}`}
                  data-testid="age-cutoff-date-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Default: Sept 1, {formData.year}
                </p>
              </div>

              {/* Age Validation Method Selection */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <Label className="text-base font-semibold">Age Eligibility Validation Method</Label>
                <p className="text-xs text-slate-500 mb-3">Choose how player age eligibility is determined</p>
                
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="age_validation_method"
                      value="auto_cutoff"
                      checked={formData.age_validation_method === 'auto_cutoff'}
                      onChange={(e) => setFormData({ ...formData, age_validation_method: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Auto Calculate (using cutoff date above)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="age_validation_method"
                      value="date_range"
                      checked={formData.age_validation_method === 'date_range'}
                      onChange={(e) => setFormData({ ...formData, age_validation_method: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Date Range (Born After/Before per category)</span>
                  </label>
                </div>

                {/* Date Range Configuration */}
                {formData.age_validation_method === 'date_range' && (
                  <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Age Category Date Ranges</p>
                        <p className="text-xs text-slate-500">Girls get 2 years extra (earlier born_after date)</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Initialize with NAYCA 2026 defaults
                          const year = formData.year;
                          const naycaDefaults = [
                            { category: 'u11', boys_born_after: `${year-12}-09-01`, boys_born_before: `${year-8}-09-01`, girls_born_after: `${year-14}-09-01`, girls_born_before: `${year-8}-09-01` },
                            { category: 'u13', boys_born_after: `${year-14}-09-01`, boys_born_before: `${year-10}-09-01`, girls_born_after: `${year-16}-09-01`, girls_born_before: `${year-10}-09-01` },
                            { category: 'u15', boys_born_after: `${year-16}-09-01`, boys_born_before: `${year-12}-09-01`, girls_born_after: `${year-18}-09-01`, girls_born_before: `${year-12}-09-01` },
                            { category: 'u17', boys_born_after: `${year-18}-09-01`, boys_born_before: `${year-14}-09-01`, girls_born_after: `${year-20}-09-01`, girls_born_before: `${year-14}-09-01` },
                          ];
                          setFormData({ ...formData, age_category_date_ranges: naycaDefaults });
                        }}
                      >
                        Load NAYCA Defaults
                      </Button>
                    </div>

                    {/* Date ranges table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2 font-medium">Category</th>
                            <th className="text-left py-2 px-2 font-medium" colSpan="2">Boys</th>
                            <th className="text-left py-2 px-2 font-medium" colSpan="2">Girls (+2 yrs)</th>
                            <th className="py-2 px-2"></th>
                          </tr>
                          <tr className="border-b text-xs text-slate-500">
                            <th></th>
                            <th className="text-left py-1 px-2">Born After</th>
                            <th className="text-left py-1 px-2">Born Before</th>
                            <th className="text-left py-1 px-2">Born After</th>
                            <th className="text-left py-1 px-2">Born Before</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.age_category_date_ranges || []).map((range, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2 px-2 font-medium uppercase">{range.category}</td>
                              <td className="py-2 px-2">
                                <Input
                                  type="date"
                                  value={range.boys_born_after || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.age_category_date_ranges];
                                    updated[idx] = { ...updated[idx], boys_born_after: e.target.value };
                                    setFormData({ ...formData, age_category_date_ranges: updated });
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <Input
                                  type="date"
                                  value={range.boys_born_before || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.age_category_date_ranges];
                                    updated[idx] = { ...updated[idx], boys_born_before: e.target.value };
                                    setFormData({ ...formData, age_category_date_ranges: updated });
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <Input
                                  type="date"
                                  value={range.girls_born_after || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.age_category_date_ranges];
                                    updated[idx] = { ...updated[idx], girls_born_after: e.target.value };
                                    setFormData({ ...formData, age_category_date_ranges: updated });
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <Input
                                  type="date"
                                  value={range.girls_born_before || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.age_category_date_ranges];
                                    updated[idx] = { ...updated[idx], girls_born_before: e.target.value };
                                    setFormData({ ...formData, age_category_date_ranges: updated });
                                  }}
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = formData.age_category_date_ranges.filter((_, i) => i !== idx);
                                    setFormData({ ...formData, age_category_date_ranges: updated });
                                  }}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Add new category */}
                    <div className="flex gap-2">
                      <Select
                        onValueChange={(value) => {
                          const existing = formData.age_category_date_ranges || [];
                          if (!existing.find(r => r.category === value)) {
                            setFormData({
                              ...formData,
                              age_category_date_ranges: [
                                ...existing,
                                { category: value, boys_born_after: '', boys_born_before: '', girls_born_after: '', girls_born_before: '' }
                              ]
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-[180px] h-8">
                          <SelectValue placeholder="Add category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {/* iter242: previously only odd-year categories were
                              selectable here. Tournaments configured with
                              even-year groupings (U10/U12/U14/U16/U18) +
                              Beginner had no way to add a date-range row
                              for them. Mirror the Age Categories checkbox
                              list so admins can pick any supported group. */}
                          {['beginner', 'u10', 'u11', 'u12', 'u13', 'u14', 'u15', 'u16', 'u17', 'u18', 'u19', 'open'].map(cat => (
                            <SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* Start and End Date in one row */}
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="end-date-input"
                />
              </div>

              {/* Registration Start and End Date in one row */}
              <div>
                <Label>Registration Start</Label>
                <Input
                  type="date"
                  value={formData.registration_start_date}
                  onChange={(e) => setFormData({ ...formData, registration_start_date: e.target.value })}
                  data-testid="reg-start-date-input"
                />
              </div>

              <div>
                <Label>Registration End</Label>
                <Input
                  type="date"
                  value={formData.registration_end_date}
                  onChange={(e) => setFormData({ ...formData, registration_end_date: e.target.value })}
                  data-testid="reg-end-date-input"
                />
              </div>

              <div className="col-span-2">
                <Label>Tournament Details</Label>
                <RichTextEditor
                  value={formData.details}
                  onChange={(value) => setFormData({ ...formData, details: value })}
                  placeholder="Enter tournament details, rules, requirements..."
                  minHeight="150px"
                  maxHeight="300px"
                  data-testid="tournament-details-input"
                />
              </div>

              <div className="col-span-2">
                <Label className="mb-2 block">Age Categories</Label>
                <div className="grid grid-cols-4 gap-2">
                  {AGE_CATEGORIES.map(cat => (
                    <div key={cat.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={cat.value}
                        checked={formData.age_categories.includes(cat.value)}
                        onCheckedChange={() => toggleAgeCategory(cat.value)}
                      />
                      <label htmlFor={cat.value} className="text-sm cursor-pointer">
                        {cat.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Division Settings */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Division-Based Tournament
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is-division-based"
                      checked={formData.is_division_based}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        is_division_based: checked,
                        divisions: checked ? ['Division A', 'Division B'] : []
                      })}
                      data-testid="is-division-based-checkbox"
                    />
                    <label htmlFor="is-division-based" className="text-sm cursor-pointer">
                      Enable divisions
                    </label>
                  </div>
                </div>
                
                {formData.is_division_based && (
                  <div className="space-y-3 bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Add division name"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            if (!formData.divisions.includes(e.target.value.trim())) {
                              setFormData({
                                ...formData,
                                divisions: [...formData.divisions, e.target.value.trim()]
                              });
                            }
                            e.target.value = '';
                          }
                        }}
                        data-testid="division-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const input = document.querySelector('[data-testid="division-input"]');
                          if (input && input.value.trim() && !formData.divisions.includes(input.value.trim())) {
                            setFormData({
                              ...formData,
                              divisions: [...formData.divisions, input.value.trim()]
                            });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.divisions.map((div, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {div}
                          <button
                            onClick={() => setFormData({
                              ...formData,
                              divisions: formData.divisions.filter((_, i) => i !== idx)
                            })}
                            className="ml-1 hover:text-red-600"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">
                      Teams will be organized into these divisions for each age category
                    </p>
                  </div>
                )}
              </div>

              {/* Play-Up Registration */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Allow Play-Up Registration
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Players can register for one age category above their natural category (e.g., U11 player can join U11 and U13)
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="allow-play-up"
                      checked={formData.allow_play_up}
                      onCheckedChange={(checked) => setFormData({ ...formData, allow_play_up: checked })}
                      data-testid="allow-play-up-checkbox"
                    />
                    <label htmlFor="allow-play-up" className="text-sm cursor-pointer">
                      Enable
                    </label>
                  </div>
                </div>

                {/* iter348 — Payment required to register */}
                <div className="flex items-start justify-between gap-4 mt-3 pt-3 border-t">
                  <div>
                    <Label htmlFor="payment-required" className="cursor-pointer">
                      Require Payment to Register
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      When enabled, a parent&apos;s registration is only created after a successful payment. Admins can still add players without payment.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <Checkbox
                      id="payment-required"
                      checked={!!formData.payment_required_for_registration}
                      onCheckedChange={(checked) => setFormData({ ...formData, payment_required_for_registration: checked })}
                      data-testid="payment-required-checkbox"
                    />
                    <label htmlFor="payment-required" className="text-sm cursor-pointer">
                      Enable
                    </label>
                  </div>
                </div>
              </div>

              {/* Target Programs for Notifications */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <Label className="mb-2 block flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Announce to Parents
                </Label>
                {!showEditDialog && (
                  <div className="flex items-start justify-between gap-4 mb-3 p-3 bg-slate-50 border rounded-lg">
                    <div>
                      <Label htmlFor="notify-parents-create" className="cursor-pointer">
                        Send announcement email now
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Off by default. When enabled, parents receive a &quot;New Tournament&quot; email as soon as this tournament is created. Leave off for admin-only / hand-picked tournaments.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 shrink-0">
                      <Checkbox
                        id="notify-parents-create"
                        checked={!!formData.notify_parents}
                        onCheckedChange={(checked) => setFormData({ ...formData, notify_parents: checked })}
                        data-testid="notify-parents-checkbox"
                      />
                      <label htmlFor="notify-parents-create" className="text-sm cursor-pointer">
                        Enable
                      </label>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mb-3">
                  Optionally limit the announcement to parents with children enrolled in specific programs.
                  Leave empty to email all parents.
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {programs.length === 0 ? (
                    <p className="text-sm text-slate-500 col-span-2">No programs available</p>
                  ) : (
                    programs.map(program => (
                      <div key={program.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`program-${program.id}`}
                          checked={formData.target_program_ids.includes(program.id)}
                          onCheckedChange={() => toggleTargetProgram(program.id)}
                        />
                        <label htmlFor={`program-${program.id}`} className="text-sm cursor-pointer">
                          {program.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.target_program_ids.length > 0 && (
                  <p className="text-xs text-blue-600 mt-2">
                    ✓ {formData.target_program_ids.length} program(s) selected — only parents in these programs will be emailed{!showEditDialog ? ' (if the announcement is enabled above)' : ''}
                  </p>
                )}
                {formData.target_program_ids.length === 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    No program filter — the announcement will reach all parents{!showEditDialog ? ' (only if enabled above)' : ''}
                  </p>
                )}
              </div>

              {/* Initial Payment Amount - Highlighted at bottom */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <Label className="text-emerald-800 font-semibold flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                    Initial Payment Amount
                  </Label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-medium">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.initial_payment_amount}
                        onChange={(e) => setFormData({ ...formData, initial_payment_amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (isNaN(val) || val < 0) setFormData(prev => ({ ...prev, initial_payment_amount: 0 }));
                        }}
                        className="pl-7 text-lg font-semibold border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500"
                        data-testid="initial-payment-input"
                      />
                    </div>
                    <span className="text-emerald-700 text-sm">per player</span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-2">
                    Amount collected from parents during tournament registration
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={showEditDialog ? handleUpdate : handleCreate} data-testid="save-tournament-btn">
              {showEditDialog ? 'Update' : 'Create'} Tournament
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default AdminTournamentFormDialog;
